/**
 * RECIBO — pantalla e impresión.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  DOS CAMINOS PARA IMPRIMIR, Y SOFTFI ELIGE SOLO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. **Impresión directa (ESC/POS).** Si esta caja tiene una impresora térmica
 *    emparejada (Ajustes → Impresora), le mandamos los comandos crudos: el
 *    ticket sale al instante, sin diálogo, y la impresora corta el papel sola.
 *    Es la experiencia de un punto de venta de verdad.
 *
 * 2. **Impresión del navegador.** Si no hay impresora emparejada — o si el
 *    aparato es un iPhone, donde esto no existe — se usa `window.print()` con un
 *    CSS de 58 u 80 mm. Sale el diálogo y hay que cortar el papel a mano, pero
 *    funciona en todas partes y con cualquier impresora instalada en el sistema.
 *
 * El botón es el mismo: Softfi elige el camino según lo que haya. Y el camino 2
 * queda siempre disponible como respaldo, porque una impresora que se quedó sin
 * papel no puede dejar a un cliente sin su recibo.
 *
 * ⚠️ El recibo se lee del documento CONGELADO que devuelve el backend, no se
 * recalcula. Así una reimpresión de hace dos años sale idéntica al papel original.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Printer, Share2, Zap } from "lucide-react";
import { Boton, Insignia, cx } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { ConDatosAlLado } from "@/components/HojaCarta";
import { fechaEnPalabras } from "@/components/PiezasDeCuenta";
import { monedas as endpointMonedas, negocio } from "@/lib/endpoints";
import { comoFormato, formatMoney, formatRate, toNumber, type FormatoMoneda, monedaImpresa } from "@/lib/money";
import { imprimirHoja, type FormatoDeHoja } from "@/lib/imprimirHoja";
import { conImpuestosDeLaVenta, llevaMarcaDeExento, nombresDeLosExentos } from "@/lib/exentos";
import type { Sale, Tenant } from "@/lib/types";
import { useImpresora } from "@/stores/impresora";
import { useAuth } from "@/stores/auth";
import { guardarPreferencia, leerPreferencias, type CodigoEnRecibo } from "@/lib/preferenciasDelRecibo";
import { aviso } from "@/stores/ui";
import { componerTicket, construirTicket, ticketComoPdf } from "./ticketEscpos";
import { TicketEnLineas } from "@/lib/ticketLineas";
import type { AnchoPapel, PaginaDeCodigos } from "@/lib/escpos";
import { ReciboEnCarta, imprimirReciboEnCarta, reciboEnCartaComoPdf } from "./ReciboEnCarta";
import { enviarPdf } from "@/lib/compartirPdf";

export function ModalRecibo({
  venta,
  onCerrar,
  recienCreada = false,
  textoCerrar,
}: {
  venta: Sale;
  onCerrar: () => void;
  recienCreada?: boolean;
  /** Qué dice el botón de cerrar. Ver la nota del pie. */
  textoCerrar?: string;
}) {
  const { data: datosNegocio } = useQuery({ queryKey: ["negocio"], queryFn: negocio.obtener });

  // Las monedas se piden para dar formato a los importes del papel con los
  // decimales correctos de cada una (los pesos colombianos no llevan céntimos).
  const { data: listaMonedas } = useQuery({
    queryKey: ["monedas"],
    queryFn: endpointMonedas.listar,
  });

  const config = useImpresora((e) => e.config);
  const conectada = useImpresora((e) => e.conectada);
  const imprimiendo = useImpresora((e) => e.imprimiendo);
  const enviarAImpresora = useImpresora((e) => e.imprimir);
  const cambiarImpresora = useImpresora((e) => e.cambiar);

  const [imprimiendoDirecto, setImprimiendoDirecto] = useState(false);
  /*
    ⚠️ LOS FORMATOS SE ELIGEN, NO IMPRIMEN (lo pidió el dueño, 2026-09-29).

    Antes cada botón —«Rollo 80 mm», «Rollo 58 mm», «Hoja carta»— abría el
    archivo apenas se tocaba. Ahora se elige, la vista previa enseña ESE
    papel, y lo imprime «Imprimir». Sin elegir en esta ventana, lo recordado.
  */
  /*
    ⚠️ LO ÚLTIMO QUE ELIGIÓ ESTA PERSONA, EN ESTE APARATO (2026-10-07).

    Lo pidió el dueño: «que quede guardada las últimas opciones del usuario».
    Antes se mandaban al negocio por una ruta que las ignoraba (ver
    `lib/preferenciasDelRecibo.ts`), y por eso nunca salía marcado el papel.
  */
  const negocioId = useAuth((e) => e.tenant?.id);
  const usuarioId = useAuth((e) => e.user?.id);
  const [preferencias] = useState(() => leerPreferencias(negocioId, usuarioId));
  const recordar = (cambio: Parameters<typeof guardarPreferencia>[2]) =>
    guardarPreferencia(negocioId, usuarioId, cambio);
  const [formatoElegido, setFormatoElegido] = useState<FormatoDeHoja | null>(preferencias.formato ?? null);
  /** El código de cada producto en el papel. Sin elegir, el papel de siempre. */
  const [codigoDeProducto, setCodigoDeProducto] = useState<CodigoEnRecibo | null>(preferencias.codigo ?? null);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EL DINERO SE FORMATEA. ANTES SALÍA CRUDO EN EL PAPEL.
    ═══════════════════════════════════════════════════════════════════════════

    El backend manda los importes como los guarda: `22.0000`. El ticket térmico
    ya los pasaba por `formatMoney`, pero esta vista —que es la que se imprime
    cuando NO hay térmica, o sea el camino que funciona en todas partes— los
    pintaba tal cual.

    Resultado: la misma venta salía «$22,00» en una impresora y «22.0000 USD»
    en la otra. Y un recibo que dice 22.0000 no parece de un negocio serio;
    parece una pantalla de programador que alguien imprimió por error.

    Cada moneda tiene sus decimales: el peso colombiano no lleva céntimos.
  */
  const formatoDe = (codigo: string): FormatoMoneda =>
    (listaMonedas ?? []).map(comoFormato).find((m) => m.code === codigo) ?? {
      code: codigo,
      symbol: codigo,
      decimals: 2,
      symbol_before: true,
    };
  const dinero = (valor: string, codigo = venta.currency_code) =>
    formatMoney(valor, formatoDe(codigo), { sinSimbolo: true });

  /** Hay impresora térmica lista en esta caja. */
  const hayDirecta = config.conexion !== null && conectada;

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EL ANCHO PUEDE ESTAR SIN ELEGIR, Y HAY QUE NOTARLO
    ═══════════════════════════════════════════════════════════════════════════

    Antes esto caía en `?? "80"`, así que un negocio recién creado veía «Rollo
    80 mm» marcado sin que nadie lo hubiera tocado. Lo vio el dueño: «no debería
    salir preseleccionada 80 si nunca la han seleccionado».

    Y no es cosmético. Un ajuste marcado se lee como «esto ya está resuelto»:
    quien tiene un rollo de 58 imprime en 80 sin mirar, el ticket sale cortado
    por el lado, y la culpa se la lleva la impresora.

    Sin elegir no se marca ninguno. El primero que se use queda recordado — así
    se pregunta una sola vez, la primera, que es cuando la persona tiene el
    rollo delante y sabe cuál es.
  */
  const elegido = hayDirecta
    ? config.ancho
    : (datosNegocio?.settings?.ticket_width ?? null);
  // Para imprimir hace falta un número sí o sí: si no hay elegido se usa 80,
  // pero eso NO se enseña como si estuviera decidido.
  const ancho = elegido ?? "80";
  /** El papel de esta ventana: el que se tocó, o el recordado. */
  const formato = (formatoElegido ?? ancho) as FormatoDeHoja;
  /** Qué botón se ve marcado. Sin nada elegido nunca, ninguno (ver arriba). */
  const marcado = formatoElegido ?? elegido;

  /*
    ═══════════════════════════════════════════════════════════════════════════
     EN QUÉ MONEDA SALE EL PAPEL (lo pidió el dueño, 2026-09-25)
    ═══════════════════════════════════════════════════════════════════════════

    En dólares o en bolívares, sin importar con qué se pagó. Solo se ofrecen
    las monedas de las que la venta sabe la tasa DE ESE DÍA (`venta.tasas`):
    convertir una venta de ayer con la tasa de hoy daría otro papel. La
    elección queda recordada en el negocio, como el ancho del rollo.
  */
  const monedasDelPapel = [venta.currency_code, ...Object.keys(venta.tasas ?? {})];
  const [monedaElegida, setMonedaElegida] = useState<string | null>(preferencias.moneda ?? null);
  const codigoDelPapel =
    [monedaElegida, datosNegocio?.settings?.ticket_moneda].find(
      (c): c is string => Boolean(c) && monedasDelPapel.includes(c as string),
    ) ?? venta.currency_code;
  const monedaDelPapel =
    codigoDelPapel !== venta.currency_code
      ? { codigo: codigoDelPapel, tasa: toNumber(venta.tasas?.[codigoDelPapel] ?? "0") }
      : null;
  const simboloDe = (codigo: string) =>
    (listaMonedas ?? []).find((m) => m.currency_code === codigo)?.symbol ?? codigo;

  /**
   * Recuerda el papel que se acaba de elegir, para no volver a preguntar.
   *
   * ⚠️ TAMBIÉN EN LA IMPRESORA DEL APARATO, Y NO ES REDUNDANCIA: si hay una
   * térmica conectada a esta caja, su ancho es el que manda al imprimir
   * directo. Elegir el rollo es elegir el rollo: si la persona ya dijo cuál
   * es, vale para todo.
   */
  function recordarPapel(v: FormatoDeHoja) {
    recordar({ formato: v });
    // La hoja carta no es un rollo: no dice nada del papel de la impresora.
    if (v !== "carta" && v !== config.ancho) cambiarImpresora({ ancho: v });
  }

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EL PDF: IMPRIMIR SIN TOCAR DRIVERS
    ═══════════════════════════════════════════════════════════════════════════

    Lo pidió el dueño con una impresora recién comprada delante: «tiene que
    funcionar como Fina — en Fina se puede imprimir sin instalar drivers ni
    nada».

    Y era el punto flojo. La hoja HTML (`imprimirHoja`) pide el tamaño del papel
    con `@page { size: 80mm ... }`, y **el navegador descarta esa regla en
    silencio si el destino elegido no admite ese tamaño**. Con «Fax» puesto en
    Destino —que es lo que quedaba cuando su impresora no salía
    preseleccionada— el recibo salía diminuto en una hoja carta.

    Un PDF lleva su geometría dentro, en el `MediaBox`. No es una sugerencia al
    navegador: es el documento diciendo cuánto mide. Se abre con el visor del
    sistema y se imprime con el driver que Windows puso solo — sin Zadig, sin
    reemplazar nada, sin sacar la impresora de la lista de Windows.

    Se abre en pestaña nueva y no se descarga: el cajero quiere imprimir ahora,
    no tener un archivo en Descargas. Si el navegador bloquea la pestaña, se
    cae a la hoja HTML de siempre — nunca se le deja sin salida.
  */
  function imprimirPdf(formato: FormatoDeHoja): void {
    // La hoja carta no es un rollo: se imprime la que está en pantalla, con la
    // plantilla de hoja carta (logo, datos del cliente, tabla y pie).
    if (formato === "carta") {
      imprimirReciboEnCarta();
      return;
    }

    try {
      const pdf = ticketComoPdf(venta, datosNegocio, {
        ancho: formato,
        pagina: config.pagina,
        // En papel normal no hay cuchilla ni cajón que abrir: los dos son
        // órdenes de la térmica y aquí no significan nada.
        cortarPapel: false,
        abrirCajon: false,
        monedas: (listaMonedas ?? []).map(comoFormato),
        moneda: monedaDelPapel,
        codigo: codigoDeProducto,
      });

      const url = URL.createObjectURL(pdf);

      /*
        ══════════════════════════════════════════════════════════════════════
         ⚠️ SIN `noopener`, Y NO ES UN DESCUIDO
        ══════════════════════════════════════════════════════════════════════

        Esto llevaba `noopener`, y por eso el recibo se imprimía DOS VECES: se
        abría la pestaña del PDF **y además** salía el diálogo de la hoja HTML
        en la pestaña de atrás.

        La causa es una trampa del navegador que no se ve leyendo el código:
        **con `noopener`, `window.open` devuelve `null` aunque la pestaña se
        haya abierto perfectamente**. Está así en el estándar. Y aquí ese `null`
        se leía como «me la bloquearon», así que disparaba el camino de
        emergencia encima del que ya había funcionado.

        Sin `noopener` devuelve la ventana de verdad, y `null` vuelve a
        significar lo que uno espera. No hay riesgo: lo que se abre es un PDF
        que generamos nosotros, no una página ajena.
      */
      const pestana = window.open(url, "_blank");
      if (!pestana) {
        // Esta vez sí: la bloqueó el navegador. La hoja de siempre funciona.
        URL.revokeObjectURL(url);
        imprimirHoja("recibo", formato);
        return;
      }
      // ⚠️ La dirección se libera TARDE, no en el acto: soltarla enseguida deja
      // al visor sin el archivo que iba a abrir y la pestaña sale en blanco.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      // Cualquier cosa rara generando el PDF no puede dejar al cliente sin su
      // comprobante: se cae al camino de siempre y ya.
      imprimirHoja("recibo", formato);
    }
  }

  /** Manda el ticket a la impresora térmica. Devuelve si lo consiguió. */
  async function imprimirDirecto(silencioso = false): Promise<boolean> {
    setImprimiendoDirecto(true);
    try {
      // ⚠️ Sin imagen en los rollos (lo pidió el dueño, 2026-09-29): el
      // ticket térmico sale igual que la vista previa y que el PDF, que
      // nunca la llevaron. El logo del negocio va en la hoja carta.
      const bytes = construirTicket(venta, datosNegocio, {
        logo: null,
        ancho: config.ancho,
        pagina: config.pagina,
        cortarPapel: config.cortarPapel,
        // El cajón se abre solo cuando hubo efectivo de por medio: no tiene
        // sentido abrirlo si el cliente pagó todo por Pago Móvil.
        abrirCajon: config.abrirCajon && hayEfectivo(venta),
        monedas: (listaMonedas ?? []).map(comoFormato),
        moneda: monedaDelPapel,
        codigo: codigoDeProducto,
      });
      return await enviarAImpresora(bytes, { silencioso });
    } finally {
      setImprimiendoDirecto(false);
    }
  }

  /**
   * El botón "Imprimir": va por la impresora térmica si la hay, y si falla cae
   * al diálogo del navegador. Nunca deja al cajero sin salida.
   */
  async function imprimir() {
    // La hoja carta no va por la térmica: es papel de oficina.
    if (hayDirecta && formato !== "carta") {
      const salio = await imprimirDirecto();
      if (salio) return;
      // Si la térmica falló, el aviso ya se mostró; abrimos el diálogo para que
      // el recibo salga igual por la impresora del sistema.
    }
    imprimirPdf(formato);
  }

  /**
   * IMPRESIÓN AUTOMÁTICA AL COBRAR.
   *
   * Con esto activado, el recibo sale en el momento en que se confirma la venta
   * y el cajero no toca nada. `yaImprimio` evita que se repita si React vuelve a
   * dibujar el modal — imprimir dos veces gasta papel y confunde al cliente.
   */
  const yaImprimio = useRef(false);
  useEffect(() => {
    if (!recienCreada || !config.automatica || !hayDirecta) return;
    if (yaImprimio.current) return;
    // Esperamos a tener los datos del negocio: un ticket sin el nombre arriba
    // no sirve para nada.
    if (!datosNegocio) return;

    yaImprimio.current = true;
    void imprimirDirecto(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recienCreada, config.automatica, hayDirecta, datosNegocio]);

  /*
    «Enviar»: el recibo como PDF, no como texto (lo pidió el dueño, 2026-10-19).
    El papel que se está viendo, en el formato elegido. Ver `enviarPdf`. El
    texto queda solo si el PDF no se pudo armar.
  */
  async function compartir() {
    let pdf: Blob | null = null;
    try {
      pdf =
        formato === "carta"
          ? reciboEnCartaComoPdf()
          : ticketComoPdf(venta, datosNegocio, {
              ancho: formato as AnchoPapel,
              pagina: config.pagina,
              cortarPapel: false,
              abrirCajon: false,
              monedas: (listaMonedas ?? []).map(comoFormato),
              moneda: monedaDelPapel,
              codigo: codigoDeProducto,
            });
    } catch {
      pdf = null;
    }
    if (pdf) {
      await enviarPdf(pdf, `Recibo ${venta.number}`);
      return;
    }

    const texto = textoDelRecibo(venta, datosNegocio?.name ?? "", dinero, monedaDelPapel);
    try {
      // En celular abre el menú nativo (WhatsApp, correo...). Muchos negocios
      // prefieren mandar el recibo por WhatsApp antes que imprimirlo.
      if (navigator.share) {
        await navigator.share({ title: `Recibo ${venta.number}`, text: texto });
      } else {
        await navigator.clipboard.writeText(texto);
        aviso.exito("Recibo copiado", "Pégalo en WhatsApp o donde quieras.");
      }
    } catch {
      /* El usuario canceló el menú de compartir: no es un error */
    }
  }

  /*
    ⚠️ LOS FORMATOS Y LA MONEDA, SIEMPRE A LA VISTA (lo vio el dueño, 2026-09-29).

    Iban debajo del papel, y con un recibo largo había que bajar hasta el
    final para elegir el formato. Ahora van a la izquierda, arriba de los
    datos de la venta, cada opción en su bloque como en la nota de entrega.

    ═══════════════════════════════════════════════════════════════════════
     ⚠️ EL NAVEGADOR NO DEJA ELEGIR LA IMPRESORA DESDE AQUÍ
    ═══════════════════════════════════════════════════════════════════════

    Lo levantó el dueño: «no preselecciona mi impresora». Y no se puede
    arreglar con código: ningún navegador permite que una página elija el
    destino de la impresión (cualquier web podría mandarte papeles a imprimir
    sin que te enteres). Desde que el recibo es un PDF el tamaño va dentro
    del archivo; lo único que queda por decir es que la impresora se elige al
    imprimir. Callarlo deja a la persona convencida de que está roto.

    ═══════════════════════════════════════════════════════════════════════
     LOS TRES FORMATOS, SIEMPRE VISIBLES
    ═══════════════════════════════════════════════════════════════════════

    Antes solo salían si había impresora directa, y era al revés de lo que
    hacía falta: la impresión directa (WebUSB) no existe en Firefox ni en
    Safari, y un negocio con Firefox se quedaba sin salida visible. Se perdió
    un cliente por eso. Cada formato imprime una hoja con SOLO el ticket, del
    ancho del papel, y funciona igual en cualquier navegador.
  */
  const opcionesDelPapel = (
    <>
      <Bloque titulo="Imprimir en">
        <div className="flex flex-wrap gap-2 pt-1">
          {(
            [
              { v: "80", t: "Rollo 80 mm" },
              { v: "58", t: "Rollo 58 mm" },
              { v: "carta", t: "Hoja carta" },
            ] as const
          ).map((f) => (
            <OpcionDelPapel
              key={f.v}
              activa={f.v === marcado}
              onClick={() => {
                setFormatoElegido(f.v);
                // Queda recordado para la próxima venta (de esta persona, aquí).
                recordarPapel(f.v);
              }}
            >
              {f.t}
            </OpcionDelPapel>
          ))}
        </div>
      </Bloque>

      {monedasDelPapel.length > 1 && (
        <Bloque titulo="Moneda del recibo">
          <div className="flex flex-wrap gap-2 pt-1">
            {monedasDelPapel.map((codigo) => (
              <OpcionDelPapel
                key={codigo}
                activa={codigo === codigoDelPapel}
                onClick={() => {
                  setMonedaElegida(codigo);
                  recordar({ moneda: codigo });
                }}
              >
                {codigo} ({simboloDe(codigo)})
              </OpcionDelPapel>
            ))}
          </div>
        </Bloque>
      )}

      {/*
        El código de cada producto (lo pidió el dueño, 2026-10-07: «la opción de
        SKU, código de barras o nada»). Sin elegir, el papel de siempre: el rollo
        sin código y la hoja carta con su columna de SKU.
      */}
      <Bloque titulo="Código de cada producto">
        <div className="flex flex-wrap gap-2 pt-1">
          {(
            [
              { v: "sku", t: "SKU" },
              { v: "barcode", t: "Código de barras" },
              { v: "ninguno", t: "Ninguno" },
            ] as const
          ).map((c) => (
            <OpcionDelPapel
              key={c.v}
              activa={c.v === codigoDeProducto}
              onClick={() => {
                setCodigoDeProducto(c.v);
                recordar({ codigo: c.v });
              }}
            >
              {c.t}
            </OpcionDelPapel>
          ))}
        </div>
      </Bloque>
    </>
  );

  const comoSeImprime = (
    <p className="px-1 text-xs leading-relaxed text-muted print:hidden">
      {formato === "carta"
        ? "Se imprime en hoja carta, a tamaño real. Aquí se ve reducida para que quepa entera."
        : "El recibo se genera con el ancho del rollo. Al imprimir, selecciona la impresora; no requiere instalación."}
    </p>
  );

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={recienCreada ? "¡Venta registrada!" : `Venta ${venta.number}`}
      descripcion={
        recienCreada
          ? `Recibo ${venta.number}`
          : new Date(venta.created_at).toLocaleString("es-VE", {
              dateStyle: "long",
              timeStyle: "short",
            })
      }
      /*
        Ancha también recién cobrada (lo pidió el dueño, 2026-10-13: «se ve
        como si fuera de móvil, debería verse así, datos a la izquierda y lo
        demás a la derecha», señalando la nota de entrega). Antes era estrecha
        a propósito, con las opciones apiladas encima del papel; en una
        computadora se veía como la pantalla de un celular.
      */
      ancho="lg"
      pie={
        <>
          {/*
            ⚠️ «NUEVA VENTA» SOLO DONDE DE VERDAD EMPIEZA UNA.

            El dueño: «pagué una cuenta en el salón y me sale "nueva venta", y
            le doy y no manda a nada». Y era cierto: en el salón este botón
            solo cierra el recibo y te deja donde estabas. Prometía empezar
            algo y no empezaba nada.

            En la pantalla de cobrar sí es verdad —cerrar el recibo vacía el
            carrito y ya estás listo para la siguiente venta—, así que el texto
            lo pone quien abre el recibo, que es el único que sabe a dónde
            vuelve.
          */}
          <Boton variante="secundario" onClick={onCerrar}>
            {textoCerrar ?? (recienCreada ? "Nueva venta" : "Cerrar")}
          </Boton>
          <Boton variante="secundario" icono={<Share2 size={16} />} onClick={compartir}>
            Enviar
          </Boton>
          <Boton
            icono={hayDirecta ? <Zap size={16} /> : <Printer size={16} />}
            onClick={() => void imprimir()}
            cargando={imprimiendoDirecto || imprimiendo}
          >
            Imprimir
          </Boton>
        </>
      }
    >
      {/*
        Los datos a la izquierda y el papel a la derecha, como la nota de
        entrega. En el celular, uno debajo del otro y los datos primero: quien
        abre una venta vieja viene a mirar algo concreto —una referencia, quién
        la hizo— y el papel es la confirmación, no la respuesta.
      */}
      <ConDatosAlLado
        datos={
          <>
            {recienCreada && (
              <div className="rounded-[var(--radius-card)] bg-success-soft p-3 text-center">
                <p className="text-sm font-medium text-success">
                  Cobrado {dinero(venta.total)} {venta.currency_code}
                </p>
                {toNumber(venta.change_total) > 0 && (
                  <p className="mt-0.5 text-xs text-success">
                    Vuelto entregado: {dinero(venta.change_total)} {venta.currency_code}
                  </p>
                )}
              </div>
            )}
            {opcionesDelPapel}
            {/*
              Recién cobrada, solo quién y cómo pagó: lo vendido y las cuentas
              ya están en el papel de al lado, y repetirlos sería ruido.
            */}
            <DetallesDeVenta venta={venta} dinero={dinero} resumen={recienCreada} />
            {comoSeImprime}
          </>
        }
      >
        {/* El papel elegido: la hoja carta (con su propio marco), o el ticket del
            rollo dibujado desde la MISMA composición que se imprime (sin imagen:
            ver `imprimirDirecto`), en un marco igual al de la hoja. */}
        {formato === "carta" ? (
          <ReciboEnCarta venta={venta} moneda={monedaDelPapel} codigoDeProducto={codigoDeProducto} />
        ) : (
          <div className="mx-auto w-fit max-w-full overflow-x-auto rounded-[var(--radius-control)] bg-surface-2 p-3">
            <HojaDelRecibo
              venta={venta}
              negocio={datosNegocio}
              ancho={formato as AnchoPapel}
              pagina={config.pagina}
              monedas={(listaMonedas ?? []).map(comoFormato)}
              logo={null}
              moneda={monedaDelPapel}
              codigoDeProducto={codigoDeProducto}
            />
          </div>
        )}

        {venta.was_offline && (
          <div className="mt-3 text-center">
            <Insignia tono="aviso">Registrada sin conexión</Insignia>
          </div>
        )}
      </ConDatosAlLado>
    </Modal>
  );
}

/** Una opción en pastilla, la misma de la nota de entrega. */
function OpcionDelPapel({
  activa,
  onClick,
  children,
}: {
  activa: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={cx(
        "rounded-full border px-3 py-1.5 text-xs font-medium transition",
        activa
          ? "border-accent bg-accent-soft text-accent"
          : "border-line text-muted hover:border-accent hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

/**
 * ¿Entró efectivo en esta venta?
 *
 * Se usa para decidir si abrir el cajón portamonedas. Miramos el tipo del método
 * de pago y no la moneda: un cobro por Zelle en dólares no mueve el cajón, pero
 * un billete de 20 sí — aunque los dos digan "USD".
 */
/**
 * El recibo en pantalla, dibujado desde LA MISMA composición que se imprime.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ POR QUÉ ESTO DEJÓ DE SER JSX A MANO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Había TRES recibos: los bytes de la térmica, el PDF, y este de aquí escrito
 * en JSX. Los dos primeros ya salían de una sola composición —`componerTicket`,
 * y su archivo lo explica: «acabaríamos con dos recibos distintos»—. Este
 * tercero se quedó fuera de ese razonamiento.
 *
 * Y pasó exactamente lo que estaba escrito que pasaría. Al rediseñar el papel
 * —un título antes del detalle, el número del recibo en negrita, el pie
 * separado— la pantalla se quedó con el diseño viejo. El dueño aprobaba una
 * cosa mirándola y le salía otra por la impresora.
 *
 * Ahora esto es un DIBUJANTE, no un segundo recibo: recibe las líneas que
 * produce `componerTicket` y las pinta. Si mañana el papel gana una línea, la
 * pantalla la tiene sin que nadie se acuerde.
 *
 * ⚠️ Y de paso la vista previa pasa a ser honesta de verdad: los cortes de
 * línea, el ancho en columnas y la alineación son los del rollo, no una
 * aproximación con CSS. Lo que se ve es lo que sale.
 */
function HojaDelRecibo({
  venta,
  negocio,
  ancho,
  pagina,
  monedas,
  logo,
  moneda,
  codigoDeProducto,
}: {
  venta: Sale;
  negocio: Tenant | undefined;
  ancho: AnchoPapel;
  pagina: PaginaDeCodigos;
  monedas: FormatoMoneda[] | undefined;
  /** La imagen del logo, para la pantalla. En el papel la pone `componerTicket`. */
  logo: string | null | undefined;
  /** En qué moneda se pintan los importes. Ver `OpcionesTicket.moneda`. */
  moneda: { codigo: string; tasa: number } | null;
  /** El código de cada producto. Ver `OpcionesTicket.codigo`. */
  codigoDeProducto: CodigoEnRecibo | null;
}) {
  const { lineas, columnas } = useMemo(() => {
    const t = new TicketEnLineas(ancho);
    componerTicket(t, venta, negocio, {
      ancho,
      pagina,
      monedas,
      // El logo se dibuja aparte, con la imagen de verdad: `TicketEnLineas` no
      // pinta bitmaps, y en pantalla se puede enseñar mejor que en el rollo.
      logo: undefined,
      cortarPapel: false,
      abrirCajon: false,
      moneda,
      codigo: codigoDeProducto,
    });
    return { lineas: t.resultado(), columnas: t.columnasDelPapel };
  }, [venta, negocio, ancho, pagina, monedas, moneda, codigoDeProducto]);

  return (
    <div
      id="recibo"
      className={cx(
        "mx-auto bg-white p-4 font-mono text-[11px] leading-tight text-black",
        "rounded-[var(--radius-control)] border border-line",
      )}
      /*
        ⚠️ EL ANCHO EN `ch`, NO EN PÍXELES.

        Con un ancho fijo (300 px) la columna de importes se salía por la
        derecha y se veía recortada: 48 caracteres de letra monoespaciada miden
        más que eso. Y un recibo que en la vista previa aparece sin los precios
        es peor que no tener vista previa.

        `1ch` es exactamente el ancho de un carácter en esta letra, así que la
        hoja mide lo que mide el rollo — 32 o 48 columnas — y no hay número que
        cuadrar a mano cuando cambie el cuerpo de letra.
      */
      style={{ width: `calc(${columnas}ch + 2rem)` }}
    >
      {logo && (
        <img
          src={logo}
          alt=""
          className="mx-auto mb-2 max-h-16 object-contain"
          /* Sin color: el rollo térmico solo imprime negro, y un logo que en
             pantalla se ve azul y en el papel gris hace dudar de la impresora. */
          style={{ filter: "grayscale(1) contrast(1.3)" }}
        />
      )}

      {lineas.map((l, i) => (
        <div
          key={i}
          className={cx(
            "whitespace-pre",
            l.alineacion === "centro" && "text-center",
            l.alineacion === "derecha" && "text-right",
            l.negrita && "font-bold",
            // A doble tamaño en el papel: aquí se dobla el cuerpo de letra para
            // que la jerarquía se vea igual.
            l.doble && "text-[15px] leading-snug",
          )}
        >
          {/* Un espacio para las líneas vacías: sin él, el navegador colapsa la
              altura y los saltos del papel desaparecen de la vista previa. */}
          {l.texto === "" ? " " : l.texto}
        </div>
      ))}
    </div>
  );
}

function hayEfectivo(venta: Sale): boolean {
  return venta.payments.some((p) => p.method_type === "cash");
}

// -----------------------------------------------------------------------------

function DetallesDeVenta({
  venta,
  dinero,
  resumen = false,
}: {
  venta: Sale;
  /** El mismo formateador del recibo: un solo sitio decide cómo se ve el dinero. */
  dinero: (valor: string, codigo?: string) => string;
  /** Recién cobrada: sin lo vendido ni las cuentas, que ya están en el papel. */
  resumen?: boolean;
}) {
  const entradas = venta.payments.filter((p) => p.direction === "in");
  const vueltos = venta.payments.filter((p) => p.direction === "change");
  const hayVariasMonedas = new Set(entradas.map((p) => p.currency_code)).size > 1;
  // Los impuestos que se cobraron, y de cuáles hay algo exento: la «(E)».
  const cobrados = (venta.impuestos_de_pedido ?? []).map((t) => t.tax_id);
  const exentosDe = nombresDeLosExentos(venta.items, venta.impuestos_de_pedido ?? []);

  return (
    <div className="space-y-3">
      {venta.status === "voided" && (
        <div className="rounded-[var(--radius-card)] border border-danger/40 bg-danger-soft p-3">
          <p className="text-sm font-semibold text-danger">Esta venta está anulada</p>
          {venta.void_reason && (
            <p className="mt-0.5 text-xs text-danger/90">{venta.void_reason}</p>
          )}
          {venta.voided_at && (
            <p className="mt-1 text-[11px] text-danger/70">
              {new Date(venta.voided_at).toLocaleString("es-VE")}
            </p>
          )}
        </div>
      )}

      {/* La corregida dice a cuál reemplaza: el cliente puede tener el papel viejo. */}
      {venta.corrige_a_numero && (
        <div className="rounded-[var(--radius-card)] border border-accent/30 bg-accent-soft p-3">
          <p className="text-sm font-semibold text-accent">
            Corrige a la venta {venta.corrige_a_numero}
          </p>
          <p className="mt-0.5 text-xs text-ink-soft">Esa quedó anulada y esta es la que vale.</p>
        </div>
      )}

      <Bloque titulo="Quién">
        <Dato etiqueta="Cliente" valor={venta.customer_name ?? "Sin cliente"} />
        <Dato etiqueta="Vendió" valor={venta.sold_by_name ?? "—"} />
      </Bloque>

      {!resumen && (
      <Bloque titulo="Qué se llevó">
        <div className="divide-y divide-line">
          {venta.items.map((l) => (
            <div key={l.id} className="flex items-start gap-2 py-1.5 text-sm">
              <span className="tabular shrink-0 text-muted">{formatearCantidad(l.qty)}×</span>
              <span className="min-w-0 flex-1">
                <span className="text-ink">{l.name}</span>
                {l.variant_name && <span className="text-muted"> · {l.variant_name}</span>}
                {/*
                  «(E)»: exento de alguno de los impuestos que se cobraron.
                  Es lo que explica por qué el IVA no salió sobre todo.
                */}
                {llevaMarcaDeExento(l.exento_de, cobrados) && (
                  <span className="font-medium text-muted"> (E)</span>
                )}
                {/*
                  De cuánto fue la recarga. El total dice 750 y se recargaron
                  500: el cliente tiene que poder comprobar el suyo en el papel.
                */}
                {l.monto_base != null && (
                  <span className="tabular text-muted"> · recarga de {dinero(l.monto_base)}</span>
                )}
                {/*
                  Cobrada sin internet a otro precio que el del catálogo. Se
                  respeta lo cobrado, pero el dueño tiene que poder verlo: si
                  alguien lo cambió a mano, aquí queda.
                */}
                {l.precio_del_catalogo != null && (
                  <span className="block text-xs text-warning">
                    Cobrado sin internet a {dinero(l.unit_price)} · el catálogo decía{" "}
                    {dinero(l.precio_del_catalogo)}
                  </span>
                )}
                {toNumber(l.discount) > 0 && (
                  <span className="block text-xs text-warning">
                    descuento de {dinero(l.discount)} {venta.currency_code}
                  </span>
                )}
                {l.staff_name && (
                  <span className="block text-xs text-muted">lo atendió {l.staff_name}</span>
                )}
              </span>
              <span className="shrink-0 text-right">
                <span className="tabular block text-ink">{dinero(l.line_total)}</span>
                {/* Antes y después del IVA, como en el ticket impreso. */}
                {(() => {
                  const conIva = conImpuestosDeLaVenta(
                    toNumber(l.line_total),
                    l.exento_de,
                    venta.impuestos_de_pedido ?? [],
                  );
                  return conIva === null ? null : (
                    <span className="tabular block text-xs text-muted">
                      {dinero(String(conIva))} con IVA
                    </span>
                  );
                })()}
              </span>
            </div>
          ))}
        </div>
      </Bloque>
      )}

      {/*
        ⚠️ AQUÍ ESTÁ LA REFERENCIA, Y ES EL MOTIVO PRINCIPAL DE ESTE PANEL.

        Cuando un cliente dice «yo te transferí», lo que resuelve la discusión
        es el número de referencia. Estaba guardado desde siempre y no había
        forma de verlo sin salir a la tabla del registro.
      */}
      <Bloque titulo={entradas.length === 1 ? "Cómo pagó" : "Cómo pagó (dividido)"}>
        <div className="space-y-1.5">
          {entradas.map((pg) => (
            <div key={pg.id} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0">
                <span className="text-ink">{pg.method_name}</span>
                {pg.reference && (
                  <span className="tabular block text-xs text-muted">ref. {pg.reference}</span>
                )}
                {/* La fecha de pago acordada de una venta a crédito (2026-10-20). */}
                {pg.datos?.vence_el && (
                  <span className="block text-xs text-muted">
                    Fecha de pago: {fechaEnPalabras(pg.datos.vence_el)}
                  </span>
                )}
              </span>
              <span className="tabular shrink-0 text-ink">
                {dinero(pg.amount, pg.currency_code)} {pg.currency_code}
                {/* La equivalencia solo cuando de verdad hay mezcla: repetirla
                    cuando todo entró en una moneda es ruido. */}
                {hayVariasMonedas && pg.currency_code !== venta.currency_code && (
                  <span className="block text-xs text-muted">
                    = {dinero(pg.amount_in_primary)} {venta.currency_code}
                  </span>
                )}
              </span>
            </div>
          ))}
          {vueltos.map((pg) => (
            <div key={pg.id} className="flex justify-between gap-2 text-sm text-warning">
              <span>Vuelto en {pg.method_name}</span>
              <span className="tabular">
                −{dinero(pg.amount, pg.currency_code)} {pg.currency_code}
              </span>
            </div>
          ))}
        </div>
      </Bloque>

      {!resumen && (
      <Bloque titulo="Las cuentas">
        <div className="space-y-1 text-sm">
          <Linea etiqueta="Subtotal" valor={dinero(venta.subtotal)} moneda={venta.currency_code} />
          {toNumber(venta.discount_total) > 0 && (
            <Linea
              /*
                ⚠️ Con el código dentro de la etiqueta.

                «Descuento −1,98» no distingue las dos cosas que pueden haberlo
                puesto ahí: una promoción que decidió el dueño, o alguien que
                decidió rebajar. Y esa es justo la diferencia que importa cuando
                se revisa una venta.
              */
              etiqueta={
                venta.discount_code_text
                  ? `Descuento · ${venta.discount_code_text}`
                  : "Descuento a mano"
              }
              valor={"−" + dinero(venta.discount_total)}
              moneda={venta.currency_code}
              tono="warning"
            />
          )}
          {/*
            ⚠️ EL IGTF VA CON SU NOMBRE, NO DENTRO DE «Impuesto».

            Un «Impuesto: 0,32» no le sirve a nadie: el cliente pregunta qué es
            ese recargo que no esperaba, y el contador necesita el IVA y el IGTF
            separados. Y el nombre y la tasa salen CONGELADOS de la venta, no
            del catálogo: si el dueño cambia la tasa el mes que viene, este
            recibo tiene que seguir diciendo lo que se cobró hoy.

            Lo que queda en «Impuesto» es el resto — el de los productos —,
            porque los de pedido ya están dentro de `tax_total`. Sumarlos otra
            vez haría que el recibo no cuadrara con su propio total.
          */}
          {(() => {
            const dePedido = venta.impuestos_de_pedido ?? [];
            const sumaDePedido = dePedido.reduce((acc, t) => acc + toNumber(t.amount), 0);
            const deProductos = toNumber(venta.tax_total) - sumaDePedido;
            return (
              <>
                {deProductos > 0 && (
                  <Linea
                    etiqueta="Impuesto"
                    valor={dinero(String(deProductos))}
                    moneda={venta.currency_code}
                  />
                )}
                {dePedido.map((t) => (
                  <Linea
                    key={t.tax_id ?? t.name}
                    etiqueta={
                      /*
                        Con algo exento de este impuesto, se dice sobre cuánto
                        se calculó. Sin eso, el cliente ve un IVA que no es el
                        16 % del total y cree que se le cobró mal.
                      */
                      exentosDe.includes(t.name)
                        ? `${t.name} ${t.rate_percent}% sobre ${dinero(t.base_amount)}`
                        : `${t.name} ${t.rate_percent}%`
                    }
                    valor={dinero(t.amount)}
                    moneda={venta.currency_code}
                  />
                ))}
              </>
            );
          })()}
          <div className="flex justify-between border-t border-line pt-1 font-semibold text-ink">
            <span>Total</span>
            <span className="tabular">
              {dinero(venta.total)} {venta.currency_code}
            </span>
          </div>
        </div>
      </Bloque>
      )}

      {venta.note && (
        <Bloque titulo="Nota">
          <p className="text-sm text-muted">{venta.note}</p>
        </Bloque>
      )}
    </div>
  );
}

function Bloque({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-surface p-3">
      <p className="rotulo mb-1.5">{titulo}</p>
      {children}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex justify-between gap-2 py-0.5 text-sm">
      <span className="text-muted">{etiqueta}</span>
      <span className="min-w-0 truncate text-ink">{valor}</span>
    </div>
  );
}

function Linea({
  etiqueta,
  valor,
  moneda,
  tono,
}: {
  etiqueta: string;
  valor: string;
  moneda: string;
  tono?: "warning";
}) {
  return (
    <div className={cx("flex justify-between", tono === "warning" ? "text-warning" : "text-muted")}>
      <span>{etiqueta}</span>
      <span className="tabular">
        {valor} {moneda}
      </span>
    </div>
  );
}

function formatearCantidad(qty: string): string {
  const n = toNumber(qty);
  return Number.isInteger(n) ? String(n) : String(n);
}

/** Versión en texto del recibo, para mandar por WhatsApp. */
function textoDelRecibo(
  venta: Sale,
  nombreNegocio: string,
  /** El mismo formateador de la pantalla: lo que se manda por WhatsApp tiene
      que verse igual que el papel. */
  dinero: (valor: string, codigo?: string) => string,
  /** La moneda elegida para el papel: el mensaje dice lo mismo que el recibo. */
  moneda: { codigo: string; tasa: number } | null = null,
): string {
  const codigo = moneda?.codigo ?? venta.currency_code;
  const en = (valor: string) =>
    dinero(moneda ? String(toNumber(valor) * moneda.tasa) : valor, codigo);
  const lineas: string[] = [
    `*${nombreNegocio}*`,
    `Recibo ${venta.number}`,
    new Date(venta.created_at).toLocaleString("es-VE"),
    "",
  ];

  const cobradosEnWhatsapp = (venta.impuestos_de_pedido ?? []).map((t) => t.tax_id);
  for (const l of venta.items) {
    const marca = llevaMarcaDeExento(l.exento_de, cobradosEnWhatsapp) ? " (E)" : "";
    lineas.push(`${formatearCantidad(l.qty)} x ${l.name}${marca} — ${en(l.line_total)}`);
  }

  lineas.push("", `*TOTAL: ${en(venta.total)} ${monedaImpresa(codigo)}*`);
  if (moneda) {
    lineas.push(`(tasa ${formatRate(String(moneda.tasa))} · ${dinero(venta.total)} ${monedaImpresa(venta.currency_code)})`);
  }
  lineas.push("");

  for (const p of venta.payments.filter((x) => x.direction === "in")) {
    lineas.push(`${p.method_name}: ${dinero(p.amount, p.currency_code)} ${monedaImpresa(p.currency_code)}`);
  }
  for (const p of venta.payments.filter((x) => x.direction === "change")) {
    lineas.push(`Vuelto: ${dinero(p.amount, p.currency_code)} ${monedaImpresa(p.currency_code)}`);
  }

  lineas.push("", "Gracias por su compra");
  return lineas.join("\n");
}
